// ─────────────────────────────────────────────────────────────────────────────
// «افزودن از فهرست دارویی» — the formulary search inside the قالب نسخه editor.
//
// The visit's نسخه tab picks drugs from the تأمین‌اجتماعی catalogue and keeps
// the formulary id on every row, which is what the interaction checker and the
// insurer mapper join on. A template built by typing names has no such id, so
// applying it produced rows the checker could not see. This control gives the
// editor the same door the visit has: type a few letters, pick a row, and the
// item arrives with its `drugId`, its usual dose and its usual frequency.
//
// Deliberately small: a search box and a short result list. The visit's full
// picker (category rail, paging, batch add) is built for prescribing in a
// hurry; a template is written once, calmly, one drug at a time.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { TextInput, SkeletonList } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { LIST_DRUGS, drugNameLines } from '@parsina/framework/clinical/src/screens/Visit/Visit.shared';
import { drugCategoryLabel } from '@parsina/framework/clinical/src/lib/drugVocabulary';
import { R_CONTROL } from './SettingsKit';

const MIN_QUERY = 2;
const PAGE = 8;
const DEBOUNCE_MS = 300;

/**
 * @param excludeIds  Set of formulary ids already in the draft — shown, but not addable twice
 * @param onPick      (drug) => void
 * @param disabled    at the item limit
 */
export default function TemplateFormularySearch({ excludeIds, onPick, disabled = false }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  // Same rhythm as the visit's search: one call per pause in typing, and a
  // reply for an older query never overwrites a newer one.
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) { setRows([]); setTotal(0); setError(null); return undefined; }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await bffQuery(LIST_DRUGS, { query: q, limit: PAGE, offset: 0 });
        if (mine !== seq.current) return;
        setRows(data?.listDrugs?.drugs || []);
        setTotal(data?.listDrugs?.total || 0);
        setError(null);
      } catch (err) {
        if (mine !== seq.current) return;
        setError(t('settings.templates.loadFailed'));
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, t]);

  const active = query.trim().length >= MIN_QUERY;

  return (
    <div data-testid="template-formulary" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <TextInput
        label={t('settings.templates.addFromFormulary')}
        helperText={t('settings.templates.formularyHint')}
        placeholder={t('visit.rx.searchDrug')}
        value={query}
        onChange={(e) => setQuery(e?.target ? e.target.value : e)}
        disabled={disabled}
        data-testid="template-formulary-search"
      />

      {active && (
        <div
          data-testid="template-formulary-results"
          style={{
            border: `1px solid ${C.border}`, borderRadius: R_CONTROL, background: C.card,
            padding: 4, display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          {loading && rows.length === 0 ? (
            <SkeletonList count={2} avatar={false} trailing={0} label={t('common.state.loading')} />
          ) : error ? (
            <div role="alert" style={{ padding: '8px 10px', fontSize: '0.75rem', color: C.danger }}>{error}</div>
          ) : rows.length === 0 ? (
            <div data-testid="template-formulary-empty" style={{ padding: '8px 10px', fontSize: '0.75rem', color: C.textMuted }}>
              {t('settings.templates.formularyEmpty')}
            </div>
          ) : (
            <>
              {rows.map((drug) => {
                const inDraft = excludeIds?.has(drug.id);
                const names = drugNameLines(drug);
                return (
                  <div
                    key={drug.id}
                    data-testid="template-formulary-result"
                    data-drug-id={drug.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px',
                      borderRadius: 8, opacity: inDraft ? 0.6 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div dir="auto" style={{ fontSize: '0.78125rem', fontWeight: 700, color: C.textPrimary }}>{names.primary}</div>
                      <div style={{ fontSize: '0.65625rem', color: C.textMuted, display: 'flex', gap: 6 }}>
                        {names.secondary && <span dir="auto">{names.secondary}</span>}
                        {drug.category && <span>· {drugCategoryLabel(drug.category, t)}</span>}
                      </div>
                    </div>
                    {inDraft ? (
                      <span style={{ fontSize: '0.6875rem', color: C.textSecondary, flexShrink: 0 }}>
                        {t('settings.templates.alreadyInTemplate')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPick(drug)}
                        disabled={disabled}
                        data-testid="template-formulary-add"
                        style={{
                          height: 30, padding: '0 10px', borderRadius: 6, flexShrink: 0,
                          border: `1.5px solid ${soft(C.navy, 0.2)}`, background: soft(C.navy, 0.05),
                          color: C.navy, fontFamily: 'inherit', fontSize: '0.71875rem', fontWeight: 700,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        + {t('common.actions.add')}
                      </button>
                    )}
                  </div>
                );
              })}
              {total > rows.length && (
                <div style={{ padding: '4px 10px 6px', fontSize: '0.6875rem', color: C.textMuted }}>
                  {t('settings.templates.formularyMore', { shown: rows.length, total })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
